import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../lib/supabase';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { MessageCircle, Phone, Mail, MapPin, Briefcase, Hammer } from 'lucide-react';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import BottomNav from '../components/BottomNav';
import { useCredits } from '../lib/hooks';

const Dashboard = ({ profile, setProfile }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { credits } = useCredits(profile);
  const [applications, setApplications] = useState([]);
  const [bids, setBids] = useState([]);
  const [myApplications, setMyApplications] = useState([]);
  const [myBids, setMyBids] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, [profile]);

  const fetchDashboardData = async () => {
    if (!profile) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data: session } = await supabase.auth.getSession();
      if (!session?.session?.access_token) return;

      const headers = { Authorization: `Bearer ${session.session.access_token}` };

      if (profile.role === 'employer') {
        const { data: jobsData } = await supabase
          .from('jobs')
          .select('id')
          .eq('employer_id', profile.id);

        if (jobsData && jobsData.length > 0) {
          const allApplications = [];
          for (const job of jobsData) {
            const response = await api.get(`/api/applications/job/${job.id}`, { headers });
            if (response.data.applications) {
              allApplications.push(...response.data.applications.map(app => ({ ...app, job_id: job.id })));
            }
          }
          setApplications(allApplications);
        }
      }

      if (profile.role === 'owner') {
        const { data: gigsData } = await supabase
          .from('gigs')
          .select('id')
          .eq('owner_id', profile.id);

        if (gigsData && gigsData.length > 0) {
          const allBids = [];
          for (const gig of gigsData) {
            const response = await api.get(`/api/bids/gig/${gig.id}`, { headers });
            if (response.data.bids) {
              allBids.push(...response.data.bids.map(bid => ({ ...bid, gig_id: gig.id })));
            }
          }
          setBids(allBids);
        }
      }

      if (profile.role === 'job_seeker') {
        const { data: appsData } = await supabase
          .from('applications')
          .select('*, job:jobs(id,title,location,employer:profiles!employer_id(name,business_name))')
          .eq('seeker_id', profile.id);
        setMyApplications(appsData || []);
      }

      if (profile.role === 'contractor') {
        const { data: bidsData } = await supabase
          .from('bids')
          .select('*, gig:gigs(id,title,location,owner:profiles!owner_id(name,business_name))')
          .eq('contractor_id', profile.id);
        setMyBids(bidsData || []);
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderApplicationCard = (application) => {
    const seeker = application.seeker;

    return (
      <Card key={application.id} className="hover:shadow-lg transition-shadow" data-testid="list-card">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <Avatar className="w-12 h-12">
              <AvatarFallback>
                {seeker?.name?.[0] || 'U'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold mb-1">
                {seeker?.name || 'Applicant'}
              </h3>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone size={14} />
                  {seeker?.phone || 'Not provided'}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                  <Mail size={14} className="shrink-0" />
                  <span className="truncate">{seeker?.email || 'Not provided'}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin size={14} />
                  {seeker?.address || seeker?.town || 'Not provided'}
                </div>
              </div>
            </div>
          </div>

          {application.cover && (
            <div className="mt-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">{application.cover}</p>
            </div>
          )}

          <div className="mt-4 flex gap-2">
            <Button
              size="lg"
              className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white"
              onClick={() => navigate(`/messages?user=${seeker?.id}`)}
              data-testid="chat-button"
            >
              <MessageCircle size={18} className="mr-2" />
              Open Chat
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderBidCard = (bid) => {
    const contractor = bid.contractor;

    return (
      <Card key={bid.id} className="hover:shadow-lg transition-shadow" data-testid="list-card">
        <CardContent className="p-5">
          <div className="flex items-start gap-4 mb-4">
            <Avatar className="w-12 h-12">
              <AvatarFallback>
                {contractor?.name?.[0] || 'C'}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h3 className="font-semibold mb-1">
                {contractor?.name || 'Contractor'}
              </h3>
              <div className="space-y-1 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Phone size={14} />
                  {contractor?.phone || 'Not provided'}
                </div>
                <div className="flex items-center gap-2 text-muted-foreground min-w-0">
                  <Mail size={14} className="shrink-0" />
                  <span className="truncate">{contractor?.email || 'Not provided'}</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin size={14} />
                  {contractor?.address || contractor?.town || 'Not provided'}
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Bid Amount:</span>
              <span className="text-lg font-semibold text-primary">₱{bid.amount?.toLocaleString()}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium">Timeline:</span>
              <span className="text-sm">{bid.timeline}</span>
            </div>
          </div>

          {bid.description && (
            <div className="mb-4 p-3 bg-muted/50 rounded-lg">
              <p className="text-sm text-muted-foreground">{bid.description}</p>
            </div>
          )}

          <div className="flex gap-2">
            <Button
              size="lg"
              className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white"
              onClick={() => navigate(`/messages?user=${contractor?.id}`)}
              data-testid="chat-button"
            >
              <MessageCircle size={18} className="mr-2" />
              Open Chat
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen-safe pb-nav md:pb-0">
      <TopBar profile={profile} setProfile={setProfile} />

      <div className="px-4 py-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="font-heading text-3xl font-bold">Dashboard</h1>
          <p className="text-muted-foreground mt-1">
            {profile?.role === 'employer' && 'Manage your job postings and applications'}
            {profile?.role === 'owner' && 'Manage your projects and bids'}
            {profile?.role === 'job_seeker' && 'Track your job applications'}
            {profile?.role === 'contractor' && 'Track your project bids'}
          </p>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="spinner" />
          </div>
        ) : (
          <>
            {profile?.role === 'employer' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-semibold">Applications ({applications.length})</h2>
                  {(credits.balance < 5 && !credits.unlimited) && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/credits')} className="rounded-xl">Buy More Credits</Button>
                  )}
                </div>
                {applications.length === 0 ? (
                  <Card className="p-6 sm:p-12 text-center">
                    <Briefcase className="mx-auto mb-4 text-muted-foreground" size={48} />
                    <h3 className="font-heading text-xl font-semibold mb-2">No applications yet</h3>
                    <p className="text-muted-foreground mb-4">Post a job to start receiving applications from qualified candidates</p>
                    <Link to="/post-job">
                      <Button className="rounded-xl">Post Your First Job</Button>
                    </Link>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{applications.map(renderApplicationCard)}</div>
                )}
              </div>
            )}

            {profile?.role === 'owner' && (
              <div>
                <div className="mb-4 flex items-center justify-between">
                  <h2 className="font-heading text-xl font-semibold">Bids ({bids.length})</h2>
                  {(credits.balance < 5 && !credits.unlimited) && (
                    <Button variant="outline" size="sm" onClick={() => navigate('/credits')} className="rounded-xl">Buy More Credits</Button>
                  )}
                </div>
                {bids.length === 0 ? (
                  <Card className="p-6 sm:p-12 text-center">
                    <Hammer className="mx-auto mb-4 text-muted-foreground" size={48} />
                    <h3 className="font-heading text-xl font-semibold mb-2">No bids yet</h3>
                    <p className="text-muted-foreground mb-4">Post a project to receive competitive bids from skilled contractors</p>
                  </Card>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">{bids.map(renderBidCard)}</div>
                )}
              </div>
            )}

            {profile?.role === 'job_seeker' && (
              <div>
                <h2 className="font-heading text-xl font-semibold mb-4">My Applications ({myApplications.length})</h2>
                {myApplications.length === 0 ? (
                  <Card className="p-6 sm:p-12 text-center">
                    <p className="text-muted-foreground mb-4">You haven&apos;t applied to any jobs yet</p>
                    <Button onClick={() => navigate('/jobs')} className="rounded-xl">Browse Jobs</Button>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {myApplications.map((app) => (
                      <Card key={app.id}>
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-lg mb-1">{app.job?.title}</h3>
                              <p className="text-sm text-muted-foreground">{app.job?.employer?.business_name || app.job?.employer?.name || 'Employer'} • {app.job?.location}</p>
                            </div>
                            <Badge>{app.status}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}

            {profile?.role === 'contractor' && (
              <div>
                <h2 className="font-heading text-xl font-semibold mb-4">My Bids ({myBids.length})</h2>
                {myBids.length === 0 ? (
                  <Card className="p-6 sm:p-12 text-center">
                    <p className="text-muted-foreground mb-4">You haven&apos;t submitted any bids yet</p>
                  </Card>
                ) : (
                  <div className="space-y-4">
                    {myBids.map((bid) => (
                      <Card key={bid.id}>
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-lg mb-1">{bid.gig?.title}</h3>
                              <p className="text-sm text-muted-foreground">{bid.gig?.owner?.business_name || bid.gig?.owner?.name || 'Project Owner'} • {bid.gig?.location}</p>
                              <p className="text-sm font-semibold text-primary mt-2">Your bid: ₱{bid.amount?.toLocaleString()}</p>
                            </div>
                            <Badge>{bid.status}</Badge>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <BottomNav />
    </div>
  );
};

export default Dashboard;